import { useEffect, useState, type CSSProperties } from "react";
import type { Game } from "@engine/core/Game";

export interface ActionSlot {
  /** Unique slot id (e.g. "1", "2", "Q", "E"). */
  key: string;
  /** Hotkey label shown in the slot corner. */
  hotkey: string;
  /** Display name (also used in the title tooltip). */
  name: string;
  /** Optional cooldown end timestamp (ms epoch from performance.now). */
  cooldownEndsAt?: number;
  /** Cooldown duration (s) used to draw the radial sweep correctly. */
  cooldownDurationS?: number;
  /** Single-letter / emoji icon — keeps things font-only, no atlas needed. */
  iconChar?: string;
  /** Color tint of the slot border. */
  color?: string;
  /** Called when the user clicks the slot or presses the hotkey. */
  onActivate?: () => void;
}

interface Props {
  game?: Game | undefined;
  slots: ActionSlot[];
}

const wrapStyle: CSSProperties = {
  position: "fixed",
  bottom: 64,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: 6,
  padding: "8px 10px",
  background: "rgba(0,0,0,0.55)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  zIndex: 990,
  pointerEvents: "auto",
};

const slotStyle = (color?: string): CSSProperties => ({
  position: "relative",
  width: 44,
  height: 44,
  borderRadius: 6,
  border: `2px solid ${color ?? "rgba(255,255,255,0.2)"}`,
  background: "rgba(255,255,255,0.04)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
  fontSize: 22,
  color: "#e6e8ee",
  cursor: "pointer",
  userSelect: "none",
  overflow: "hidden",
});

/**
 * 8-slot action bar. Hotkeys 1..8 (configurable via `slot.hotkey`).
 * Renders a radial cooldown sweep when `cooldownEndsAt` is in the future.
 *
 *   <ActionBar game={engine?.game} slots={SLOTS} />
 */
export function ActionBar({ game, slots }: Props) {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!game) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const slot = slots.find((s) => keyMatches(e.code, s.hotkey));
      if (slot) {
        e.preventDefault();
        slot.onActivate?.();
      }
    };
    window.addEventListener("keydown", onKey);
    // Re-render to advance cooldown sweeps
    const id = window.setInterval(() => setTick((t) => t + 1), 100);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearInterval(id);
    };
  }, [game, slots]);

  return (
    <div style={wrapStyle} data-testid="action-bar">
      {slots.map((s) => {
        const now = performance.now();
        const cd = s.cooldownEndsAt ?? 0;
        const remaining = Math.max(0, cd - now);
        const dur = (s.cooldownDurationS ?? 0) * 1000;
        const cdFraction = dur > 0 ? Math.min(1, remaining / dur) : 0;
        return (
          <div
            key={s.key}
            style={slotStyle(s.color)}
            title={s.name}
            onClick={() => s.onActivate?.()}
          >
            <span style={{ filter: cdFraction > 0 ? "grayscale(0.8) brightness(0.6)" : undefined }}>
              {s.iconChar ?? "?"}
            </span>
            {cdFraction > 0 && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `conic-gradient(rgba(0,0,0,0.55) ${360 * cdFraction}deg, transparent 0)`,
                  pointerEvents: "none",
                }}
              />
            )}
            <span
              style={{
                position: "absolute",
                bottom: 1,
                right: 3,
                fontSize: 10,
                color: "rgba(255,255,255,0.65)",
              }}
            >
              {s.hotkey}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function keyMatches(code: string, hotkey: string): boolean {
  // "1".."9" / "0" → "Digit1".."Digit0"
  if (/^[0-9]$/.test(hotkey)) return code === `Digit${hotkey}`;
  // letters
  if (/^[A-Za-z]$/.test(hotkey)) return code === `Key${hotkey.toUpperCase()}`;
  // already a code
  return code === hotkey;
}
